import {
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {
  MatColumnDef,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { EgButtonComponent } from '../../shared/components/eg-button/eg-button.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UserManagementDialogComponent } from './user-management-dialog/user-management-dialog.component';
import { HighlightStatusDirective } from '../../shared/directives/highlight-status.directive';
import { UserManagementService } from '../../service/user-management.service';
import { debounceTime, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { EgTableComponent } from '../../shared/components/eg-table/eg-table.component';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatColumnDef,
    MatButtonModule,
    MatSortModule,
    MatIconModule,
    MatMenuModule,
    EgButtonComponent,
    MatFormField,
    MatLabel,
    MatInputModule,
    FormsModule,
    HighlightStatusDirective,
    EgTableComponent
  ],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
})
export class UserManagementComponent implements OnInit{

  searchText !: string;
  cols: any = [
    'firstName','lastName','email','role','actions'
  ];
  displayedColumns = [
    {
      key: 'firstName',
      label: 'First Name',
    },
    {
      key: 'lastName',
      label: 'Last Name',
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'role',
      label: 'Role',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ];
  dataSource : any = [];
  isLoading: boolean = false;
  start = 0;
  end = 10;
  pageSize = 10;
  usersList = [];
  private readonly searchSubject: Subject<string> = new Subject();
  constructor(private readonly userManagementService: UserManagementService, private readonly toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.getUserList(this.start, this.pageSize, ''); 
    this.searchSubject.pipe(debounceTime(300)).subscribe((keyword) => {
      if (keyword.length >= 3) {
        this.getUserList(this.start, this.pageSize, keyword);
      } else if (keyword.length === 0) {
        this.getUserList(this.start, this.pageSize, ''); // Load all applicants if search is cleared
      }
    });
  }
  columns = ['firstName', 'lastName', 'email', 'role'];
  totalElements = 0;
  columnKeyValues = [
    {
      firstName: 'First Name',
      lastName: 'Last Name',
      role: 'Role',
      email: 'Email'
    }
  ]
  usersListCopy: any = [];
  pageIndex = 0;
  getUserList(start:any, limit:any, keyword?:string) {
    this.isLoading = true;
    this.userManagementService.getUsers(start.toString(), limit, keyword).subscribe({
      next: (data:any) => {
        this.isLoading = false;
        if(data && data.content) {
          this.dataSource = new MatTableDataSource<Element>(data.content);
          this.usersList = data.content;
          this.usersListCopy = this.usersList?.map((
            user: { userId: string; firstName: string;middleName: string; lastName:string;role:string;email:string;actionUsers:[] }) => (
              { userId: user.userId, 
                firstName: user.firstName,
                middleName: user.middleName,
                lastName: user.lastName,
                role: user.role,
                email: user.email,
                actions: ['Edit', 'View', 'Delete']
              }));
          this.start = 0;
          this.end = data?.numberOfElements
          this.totalElements = data?.totalElements;
        }
        else {
          this.usersList = [];
          this.usersListCopy = []
        }
      },
      error:(error) => {
        this.isLoading = false;
        console.log(error);
        this.toastr.error('Error fetching users');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }
  
  addUser(result: any) {
    const agencyString = sessionStorage.getItem('agency');
    let agency = agencyString ? JSON.parse(agencyString) : null;
    this.userManagementService.addUser(result, agency).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.toastr.success('User added successfully');
        this.getUserList(this.start, this.pageSize, this.searchText ? this.searchText : '');
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
        this.toastr.error('Error adding user');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }

  editUser(result: any) {
    const agencyString = sessionStorage.getItem('agency');
    let agency = agencyString ? JSON.parse(agencyString) : null;
    this.userManagementService.updateUser(result.data, result.userId, agency).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.toastr.success('User updated successfully');
        this.getUserList(this.start, this.pageSize, this.searchText ? this.searchText : '');
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
        this.toastr.error('Error updating user');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }

  deleteUser(id: any) {
    this.userManagementService.deleteUser(id).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.toastr.success('User deleted successfully');
        this.getUserList(this.start, this.pageSize, this.searchText ? this.searchText : '');
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
        this.toastr.error('Error deleting user');
        this.userManagementService.redirectToLogin(error,'');
      }
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const keyword = input.value;
    this.searchSubject.next(keyword);
  }

  clearSearch() {
    this.searchText = '';
    this.getUserList(this.start, this.pageSize, '');
  }
  getChangedUsersList(event: any) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.getUserList(event.pageIndex, this.pageSize, this.searchText ? this.searchText : '');
  }
  performUserOperations(event: any) {
    this.openDialog(event.param, event.element);
  }
  readonly dialog = inject(MatDialog);
  openDialog(param: any, element: any) {
    const dialogRef = this.dialog.open(UserManagementDialogComponent,
      {
        width: param === 'delete' ? '20rem': '48rem',
        minWidth: param === 'delete' ? '20rem': '48rem',
        height: param === 'delete' ? '12rem': '29rem',
        data: {
          param : param,
          header: param === 'edit' ? 'Edit User' : param === 'view' ? 'View User' : param === 'add' ? 'Add User' : 'Delete User',
          content: [
            element
          ],
          actions: ['Submit', 'Cancel']
        }
      }
    );
    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.isLoading = true;
        console.log(result);
        if(param === 'add') {
          this.addUser(result);
        }
        else if(param === 'edit') {
          this.editUser(result);
        }
        else if(param === 'delete') {
          this.deleteUser(element.userId);
        }
      }
    });
  }
}
